/**
 * ⭐ F212 — THE JOURNEY ROW UNDER EACH PORTAL APPOINTMENT (Decision 366)
 *
 * > Pete, 2026-10-01: *"lets have the journey graph filled out on the row below it - again for
 * > both carrier and client. The journey should be dynamic, meaning in the loading / unloading
 * > portion, it should have the circle icon for loading/unloading"* · *"For inbounds, this should
 * > actually include the dock to stock journey items so the customer can see when the order was
 * > received in the WMS"*.
 *
 * The steps come from Pipeline's builder through GET portal/appointments/{id}/journey (the
 * portal's cut: times only, a client's own orders). This only draws, with JourneyPanel's own
 * Timeline, so the portal, the Scheduler's dialog and Pipeline show the same marks.
 *
 * ⚠️ LOADED WHEN THE ROW SCROLLS INTO VIEW, three at a time — F210's rule for the Operations
 * page: a page of appointments must not fire every journey at once. A live load (being
 * worked, or on site) re-reads every two minutes so the circle keeps up with the dock.
 */
import { useEffect, useRef, useState } from 'react';
import { JourneyTimelines } from '@/components/JourneyPanel';
import type { JourneyDto, JourneyStepDto } from '@/api/client';

const MAX_AT_ONCE = 3;
let running = 0;
const waiting: (() => void)[] = [];
function limited<T>(job: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const go = () => {
            running++;
            job().then(resolve, reject).finally(() => {
                running--;
                const next = waiting.shift();
                if (next) next();
            });
        };
        if (running < MAX_AT_ONCE) go(); else waiting.push(go);
    });
}

/**
 * ⭐ The Status column's words, once the journey says more than the Dock can: after an
 * inbound truck leaves, what the WMS has done with the freight. Null = keep the Dock's word.
 */
export function refinedStatus(j: JourneyDto): string | null {
    if (j.direction !== 'INBOUND') return null;
    const steps: JourneyStepDto[] = j.timelines.flatMap((t) => t.steps);
    const step = (k: string) => steps.filter((s) => s.key === k);
    const done = (k: string) => step(k).length > 0 && step(k).every((s) => s.state === 'done');
    if (done('ready')) return 'Received · ready';
    if (done('poDone')) return 'Received in WMS';
    const put = step('putaway').find((s) => s.state === 'now' || s.state === 'done');
    if (put) return put.detail ? `Receiving · ${put.detail}` : 'Receiving';
    return null;
}

export function PortalJourneyRow({
    appointmentId, live, load, onStatus,
}: {
    appointmentId: number;
    /** on site or being worked: re-read every two minutes */
    live: boolean;
    load: (appointmentId: number) => Promise<JourneyDto>;
    onStatus?: (appointmentId: number, word: string | null) => void;
}) {
    const ref = useRef<HTMLDivElement | null>(null);
    const [seen, setSeen] = useState(false);
    const [data, setData] = useState<JourneyDto | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        const el = ref.current;
        if (!el || seen) return;
        if (typeof IntersectionObserver === 'undefined') { setSeen(true); return; }
        const io = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) { setSeen(true); io.disconnect(); }
        }, { rootMargin: '200px' });
        io.observe(el);
        return () => io.disconnect();
    }, [seen]);

    useEffect(() => {
        if (!seen) return;
        let alive = true;
        limited(() => load(appointmentId))
            .then((j) => { if (alive) { setData(j); setError(null); onStatus?.(appointmentId, refinedStatus(j)); } })
            .catch((e) => { if (alive) setError(e instanceof Error ? e.message : 'The journey could not be read.'); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seen, appointmentId, tick]);

    useEffect(() => {
        if (!live || !seen) return;
        const t = setInterval(() => setTick((n) => n + 1), 120_000);
        return () => clearInterval(t);
    }, [live, seen]);

    return (
        <div ref={ref} className="min-h-[56px]">
            {error && <div className="text-[11px] text-gray-400">The journey could not be read: {error}</div>}
            {!error && !data && (
                <div className="flex items-center gap-2 py-2 text-[11px] text-gray-400" aria-live="polite">
                    <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-500" aria-hidden="true" />
                    Loading the journey…
                </div>
            )}
            {data && (
                <div className="overflow-x-auto">
                    <JourneyTimelines data={data} compact />
                </div>
            )}
        </div>
    );
}
